import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRightIcon } from '@/components/icons'
import { cx } from './internal/cx'
import './FeatureCard.css'

export type FeatureCardProps = {
  /** Decorative line icon shown in the tinted tile. */
  icon?: ReactNode
  title: string
  description?: string
  /** Action handler for non-navigational cards. */
  onClick?: () => void
  /** Route target; renders the card as a link. Takes precedence over onClick. */
  to?: string
  children?: ReactNode
}

/** Navigational or actionable feature card with icon tile and chevron. */
export function FeatureCard({ icon, title, description, onClick, to, children }: FeatureCardProps) {
  const interactive = Boolean(to) || Boolean(onClick)

  const inner = (
    <>
      {icon && <span className="ui-feature-card__icon">{icon}</span>}
      <span className="ui-feature-card__text">
        <span className="ui-feature-card__title">{title}</span>
        {description && <span className="ui-feature-card__description">{description}</span>}
        {children}
      </span>
      {interactive && (
        <span className="ui-feature-card__chevron">
          <ChevronRightIcon size={20} />
        </span>
      )}
    </>
  )

  const className = cx('ui-feature-card', interactive && 'ui-feature-card--interactive')

  if (to) {
    return (
      <Link to={to} className={className}>
        {inner}
      </Link>
    )
  }

  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {inner}
      </button>
    )
  }

  return <div className={className}>{inner}</div>
}
